import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import { CustomTitle } from '@meitu/saas-ui/es/title';
import { TextWritingType, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { useEditor } from '@/hooks/use-editor';
import { getLocaleStore, useLocaleStore } from '@/i18n';
import { type ArtTextStruct } from '@/types';
import DragPopover from '../../components/drag-popover';
import { useLayerProps } from '../../hooks';
import { ArtTextPanel } from './art-text-panel';
import { BaseEffectPresetPanel } from './base-effect-preset-panel';
import './style.less';

type Props = {
    /**
     * 文本图层 id
     */
    layerId: string;
    /**
     * 是否是国际版，用于 UI 定制
     */
    isIntl: boolean;
    /**
     * 记录id，用于埋点
     */
    recordId?: number;
    /**
     * 是否免费开放基础特效（即不展示 VIP/PRO 标识、不写入素材 id），默认 false
     */
    free?: boolean;
    /**
     * 是否隐藏基础特效预设，默认 false
     */
    hidePresets?: boolean;
    /**
     * 主题
     */
    theme: string;
    /**
     * 花字素材特效素材
     */
    artTexts?: ArtTextStruct[];
    /**
     * 关闭弹窗
     */
    onClose: () => void;
};

type TabKey = 'base' | 'art';

const getTabs = () => {
    const { $t } = getLocaleStore();

    const ret: Array<{ key: TabKey; label: string }> = [
        {
            key: 'base',
            label: $t((v) => v.panel.textPanel.textEffectBase),
        },
        {
            key: 'art',
            label: $t((v) => v.panel.textPanel.textEffectArt),
        },
    ];

    return ret;
};

const TextEffectModal: React.FC<Props> = ({
    free = false,
    hidePresets = false,
    layerId,
    isIntl,
    recordId,
    theme,
    artTexts = [],
    onClose,
}) => {
    const { $t } = useLocaleStore();

    const TABS = getTabs();

    const [tab, setTab] = useState<TabKey>('base');

    const draggleRef = useRef<HTMLDivElement>(null);

    const editor = useEditor();
    const props = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const { shapeExtension, writingMode } = layerData;

        return {
            shapeExtension,
            writingMode,
        };
    });

    const disableArtText = props?.writingMode !== TextWritingType.HORIZONTAL_TB || !!props?.shapeExtension;

    const handleTabChange = (tab: TabKey) => {
        if (tab === 'art' && disableArtText) return;

        setTab(tab);
    };

    return (
        <DragPopover draggleRef={draggleRef}>
            <div className="text-effect-modal" ref={draggleRef}>
                <div className="text-effect-modal__title">
                    <CustomTitle
                        leftNode={<div className="left">{$t((v) => v.panel.textPanel.textEffect)}</div>}
                        rightNode={
                            <div className="right" onClick={onClose}>
                                <IconFont name="CrossBlack" fontSize={12} color="var(--content-system-secondary)" />
                            </div>
                        }
                    />
                </div>
                <div className="text-effect-modal__body">
                    <div className="text-effect-modal__tab">
                        {TABS.map((item) => {
                            if (item.key === 'art' && (artTexts.length === 0 || isIntl || disableArtText)) return null;

                            return (
                                <div
                                    key={item.key}
                                    className={classNames('text-effect-modal__tab-item', {
                                        'text-effect-modal__tab-item--active': tab === item.key,
                                    })}
                                    onClick={() => handleTabChange(item.key)}
                                >
                                    {item.label}
                                </div>
                            );
                        })}
                    </div>
                    <div className="text-effect-modal__content">
                        {!hidePresets && tab === 'base' ? (
                            <BaseEffectPresetPanel layerId={layerId} isIntl={isIntl} recordId={recordId} free={free} />
                        ) : null}
                        {tab === 'art' && artTexts.length > 0 && !isIntl ? (
                            <ArtTextPanel artTexts={artTexts} layerId={layerId} recordId={recordId} theme={theme} />
                        ) : null}
                    </div>
                </div>
            </div>
        </DragPopover>
    );
};

export default React.memo(TextEffectModal);
